import { SelectedPurposeOfUseOrContact } from '../../../common/enums';
import { fetchContacts } from '../../../common/fetch';
import { scrollTo } from '../../../common/utils/browserUtils';
import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils';
import type { OrderItemPurposeOrContact, PlaceOrderItemUnit } from '../placeOrderTypes';
import type { ContactOrPurposeHandle } from '../steps/products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose';
import type { MutableRefObject } from 'react';

export enum ContactCreationType {
  CREATE_NEW_CONTACT = 'ADD_NEW',
}

export const anyRequired = (contactFormRefs: MutableRefObject<Record<string, ContactOrPurposeHandle | null>>) => {
  return Object.values(contactFormRefs.current)
    .filter(ref => ref !== null)
    .map(ref => ref?.getRequired())
    .some(value => value === true);
};

/**
 * Converts a PlaceOrderItemUnit to OrderItemPurposeOrContact for form initialization/rehydration.
 * Maps subscriptionReference (API field) to employeeNumber (form field).
 */
export const convertOrderItemToPurposeOrContact = (orderItem: PlaceOrderItemUnit): OrderItemPurposeOrContact => ({
  basketItemId: orderItem.basketItemId,
  basketItemIndex: orderItem.basketItemIndex,
  contactId: orderItem.userContact,
  costCenter: orderItem.costCenter,
  employeeNumber: orderItem.subscriptionReference,
  purposeOfUse: orderItem.purposeOfUse,
  additionalParameters: orderItem.additionalParameters,
  selected: orderItem.purposeOfUse ? SelectedPurposeOfUseOrContact.PURPOSEOFUSE : SelectedPurposeOfUseOrContact.CONTACT,
});

export const syncAllContactsToParentForm = async (
  contactFormRefs: MutableRefObject<Record<string, ContactOrPurposeHandle | null>>
) => {
  for (const ref of Object.values(contactFormRefs.current)) {
    await ref?.syncToParentForm();
  }
};

export const handleAllContactsValidate = async (
  contactFormRefs: MutableRefObject<Record<string, ContactOrPurposeHandle | null>>
) => {
  const errorIdPromises = Object.entries(contactFormRefs.current)
    .filter(([, ref]) => ref !== null)
    .filter(([, ref]) => ref?.getRequired())
    .map(async ([key, ref]) => {
      const success = await ref?.validate();
      const errors = ref?.getErrors;

      if (!success || (errors && Object.keys(errors).length !== 0)) {
        return key;
      } else {
        return undefined;
      }
    });

  const errorIds = await Promise.all(errorIdPromises);

  const firstErrorId = errorIds.filter(errorId => errorId)[0];

  if (firstErrorId) {
    Object.values(contactFormRefs.current)
      .filter(ref => ref !== null)
      .forEach(ref => ref?.scrollToContactError(firstErrorId));
    return false;
  } else {
    return true;
  }
};

export const scrollToError = (elementId?: string) => {
  let element: HTMLElement | null;

  if (elementId && elementId?.length > 0) {
    element = document.getElementById(elementId);
  } else {
    element = document.querySelector('#contactIdError, .ds-input-error, [id$="-error"]');
  }

  scrollTo(element || 0);
};

export const updateContacts = async () => {
  const mdmId = getActiveAccountMasterId()!;
  return await fetchContacts({ offset: 0 }, { mdmId });
};
